import { type FunctionComponent } from 'react';
import { type ProductAttributeGroupFieldsFragment } from '../../../__generated__/graphql-client-types';
import { type HeadingValuesType } from '../../../constants/general';
import { type ProductAttribute as ProductAttributeType } from '../../../types/product.types';
import { HelpIcon } from '../../common-components/help-icon/help-icon.component';

export type ProductAttributesProps = {
	className?: string;
	attributeGroup: ProductAttributeGroupFieldsFragment;
	headingValue?: HeadingValuesType;
};

export const Help: FunctionComponent<{ description: string | null; className?: string }> = ({ description, className = '' }) => {
	if (!description) {
		return null;
	}
	return (
		<div className={`mr2 pl3 ${className}`} data-testid="help-icon">
			<HelpIcon className="w5">
				<div className="f6">{description}</div>
			</HelpIcon>
		</div>
	);
};

export type ProductAttributeProps = {
	className?: string;
	labelClassName?: string;
	valueClassName?: string;
} & ProductAttributeType;

export const ProductAttribute: FunctionComponent<ProductAttributeProps> = ({
	className = 'striped--grey-lighter',
	labelClassName = 'w-50',
	valueClassName = 'w-50',
	name,
	description,
	values,
	units
}) => (
	<tr data-testid={name} className={`w-100 ${className ?? ''}`.trim()}>
		<td className={`${labelClassName ?? ''}`}>
			<div className="pa2 flex justify-between items-center">
				<span className="b lh-copy">{name}</span> <Help description={description} />
			</div>
		</td>
		<td className={`${valueClassName ?? ''}`}>
			{values} {units}
		</td>
	</tr>
);

export const ProductAttributes: FunctionComponent<ProductAttributesProps> = ({ attributeGroup, headingValue = 'h2' }) => {
	const { name, attributes } = attributeGroup;
	const HeadingTag = headingValue;
	return (
		<>
			<div className="dn db-ns">
				<div>
					<HeadingTag className={'f4 ma0 mt4'}>{name}</HeadingTag>
					<table className={`w-100 f6 collapse lh-solid bg-theme-white mt2`}>
						<tbody>
							{attributes?.map((attribute, i) => (
								<ProductAttribute key={i} {...attribute} />
							))}
						</tbody>
					</table>
				</div>
			</div>
			{/* Mobile only */}
			<div className="dn-ns">
				<h4 className="f5 ma0 w-100 items-center flex mt4">{name}</h4>
				<table className="w-100 f6 collapse lh-solid bg-theme-white mt2">
					<tbody>
						{attributes?.map((attribute, i) => (
							<ProductAttribute key={i} {...attribute} />
						))}
					</tbody>
				</table>
			</div>
		</>
	);
};
